import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { rbacService } from "../services/rbacService";
import { ArrowLeft, Shield, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card } from "../components/ui/card";
import toast from "react-hot-toast";
import TableSkeleton from "../components/skeletons/TableSkeleton";

// Permission descriptions for tooltips
const permissionDescriptions = {
  project: "Manage project settings, lifecycle, and access.",
  test_suits:
    "Milestones used to track testing phases, releases, or delivery checkpoints.",
  test_suits_cases_sections: "Manage test cases and their hierarchy.",
  attachments: "Upload and manage linked files.",
  runs_plan: "Create and track test executions.",
  template: "Manage test case templates and Case, Result level Fields.",
  report: "View execution and coverage reports.",
  dashboard: "View project insights and metrics.",
  configuration: "Manage project-level configurations.",
  variables: "Manage reusable test variables.",
  user: "Manage users and project access.",
  role: "Manage roles and permissions.",
  defect_management: "View and manage defects.",
  integration_management:
    "View and configure integrations (Jira, Slack, Figma).",
};

// Only include these permissions
const allowedFeatures = [
  "project",
  "test_suits",
  "test_suits_cases_sections",
  "attachments",
  "runs_plan",
  "template",
  "report",
  "dashboard",
  "configuration",
  "variables",
  "user",
  "role",
  "defect_management",
  "integration_management",
];

// Define which actions should be shown for each feature
const featureActionMap = {
  project: ["view", "add_edit", "inactive", "permanently_delete", "close"],
  test_suits: ["view", "add_edit", "permanently_delete"],
  test_suits_cases_sections: [
    "view",
    "add_edit",
    "inactive",
    "permanently_delete",
  ],
  attachments: ["view", "add_edit", "permanently_delete"],
  runs_plan: ["view", "add_edit", "inactive", "permanently_delete", "close"],
  template: ["view", "add_edit", "permanently_delete"],
  report: ["view", "add_edit", "inactive"],
  dashboard: ["view", "add_edit"],
  configuration: ["view", "add_edit"],
  variables: ["view", "add_edit", "permanently_delete"],
  user: ["view", "add_edit", "permanently_delete"],
  role: ["view", "add_edit", "permanently_delete"],
  defect_management: [
    "view",
    "add_edit",
    "inactive",
    "permanently_delete",
    "close",
  ],
  integration_management: ["view", "add_edit"],
};

// Module mapping
const moduleMapping = {
  project: "Project Management Module",
  test_suits: "Test Management Module",
  test_suits_cases_sections: "Test Management Module",
  attachments: "Test Management Module",
  runs_plan: "Test Execution Module",
  template: "Test Management Module",
  report: "Reporting Module",
  dashboard: "Dashboard Module",
  configuration: "Configuration Module",
  variables: "Configuration Module",
  user: "User Management Module",
  role: "User Management Module",
  defect_management: "Test Management Module",
  integration_management: "Configuration Module",
};

// Feature display names
const getFeatureDisplayName = (feature) => {
  const mapping = {
    project: "Project",
    test_suits: "Milestones",
    test_suits_cases_sections: "Test Suites, Sections, Cases",
    attachments: "Attachments",
    runs_plan: "Test Runs/Plans",
    template: "Test Case / Test Run Layout",
    report: "Reports",
    dashboard: "Dashboard",
    configuration: "Client Configuration",
    variables: "Variables",
    user: "Users",
    role: "Roles",
    defect_management: "Defects",
    integration_management: "Integrations",
  };
  return mapping[feature] || feature;
};

// Action display names
const getActionDisplayName = (action) => {
  const mapping = {
    view: "View (default yes)",
    add_edit: "Add/Edit",
    inactive: "Inactive",
    permanently_delete: "Permanently Delete",
    close: "Close",
  };
  return mapping[action] || action;
};

const ClientConfigurationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id");
  const clientName = searchParams.get("client_name") || "Client";
  const isFromAllClients = searchParams.get("from") === "all-clients";

  const [permissions, setPermissions] = useState({});
  const [clientConfigPermissions, setClientConfigPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Check if user is CHIEF_ADMIN
  useEffect(() => {
    // Check both role_name and role, and normalize the role name
    const userRole = user?.role_name || user?.role;
    if (!userRole) {
      toast.error("Only CHIEF_ADMIN can access Client Configuration");
      navigate("/clients");
      return;
    }
    const normalizeRole = (role) =>
      role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
    const normalizedRole = normalizeRole(userRole);
    if (normalizedRole !== "chief-admin") {
      toast.error("Only CHIEF_ADMIN can access Client Configuration");
      navigate("/clients");
    }
  }, [user, navigate]);

  // Load CLIENT_CONFIGURATION permissions
  const loadClientConfigurationPermissions = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      const response =
        await rbacService.getClientConfigurationPermissions(clientId);

      if (response.data?.permissions) {
        // Transform from module-based format to feature:action format
        const transformed = {};
        const permissionsData = response.data.permissions;

        Object.keys(permissionsData).forEach((moduleName) => {
          Object.keys(permissionsData[moduleName]).forEach((feature) => {
            if (!transformed[feature]) {
              transformed[feature] = {};
            }

            const permData = permissionsData[moduleName][feature];
            if (permData.read !== undefined)
              transformed[feature].view = permData.read;
            if (permData.write !== undefined)
              transformed[feature].add_edit = permData.write;
            if (permData.inactive !== undefined)
              transformed[feature].inactive = permData.inactive;
            if (permData.permanently_delete !== undefined)
              transformed[feature].permanently_delete =
                permData.permanently_delete;
            if (permData.close !== undefined)
              transformed[feature].close = permData.close;
          });
        });

        setClientConfigPermissions(transformed);
        setOriginalPermissions(JSON.parse(JSON.stringify(transformed)));
      }
    } catch (error) {
      console.error("Error loading CLIENT_CONFIGURATION permissions:", error);
      toast.error("Failed to load client configuration permissions");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClientConfigurationPermissions();
  }, [loadClientConfigurationPermissions]);

  // Handle permission toggle
  const handlePermissionToggle = (feature, action, currentGranted) => {
    setClientConfigPermissions((prev) => {
      const updated = { ...prev };
      if (!updated[feature]) {
        updated[feature] = {};
      }
      updated[feature] = { ...updated[feature] };

      const newValue = !currentGranted;
      updated[feature][action] = newValue;

      // If view is being disabled, disable all other actions for this feature
      if (action === "view" && !newValue) {
        updated[feature]["add_edit"] = false;
        updated[feature]["inactive"] = false;
        updated[feature]["permanently_delete"] = false;
        updated[feature]["close"] = false;
      }

      return updated;
    });
  };

  // Get permission status
  const getPermissionStatus = (feature, action) => {
    if (
      clientConfigPermissions[feature] &&
      clientConfigPermissions[feature][action] !== undefined
    ) {
      return clientConfigPermissions[feature][action] === true;
    }
    return false;
  };

  // Handle save permissions
  const handleSavePermissions = async () => {
    if (!clientId) return;

    try {
      setSaving(true);

      // Transform feature:action format to module-based format
      const permissionsByModule = {};

      allowedFeatures.forEach((feature) => {
        const moduleName = moduleMapping[feature] || "Other Module";
        if (!permissionsByModule[moduleName]) {
          permissionsByModule[moduleName] = {};
        }

        const featurePerms = clientConfigPermissions[feature] || {};
        const allowedActions = featureActionMap[feature] || [];

        // Build permission object with all action flags explicitly set
        const permObj = {
          levels: ["company"],
          meta_data: {
            description: (permissionDescriptions[feature] || "").trim() || "-",
            name: (getFeatureDisplayName(feature) || "").trim() || "-",
          },
        };

        if (allowedActions.includes("view")) {
          permObj.read = featurePerms.view === true;
        }
        if (allowedActions.includes("add_edit")) {
          permObj.write = featurePerms.add_edit === true;
        }
        if (allowedActions.includes("inactive")) {
          permObj.inactive = featurePerms.inactive === true;
        }
        if (allowedActions.includes("permanently_delete")) {
          permObj.permanently_delete = featurePerms.permanently_delete === true;
        }
        if (allowedActions.includes("close")) {
          permObj.close = featurePerms.close === true;
        }

        permissionsByModule[moduleName][feature] = permObj;
      });

      const payload = {
        data: {
          permissions: permissionsByModule,
        },
      };

      await rbacService.updateClientConfigurationPermissions(clientId, payload);
      toast.success("Client configuration permissions updated successfully");
      if (isFromAllClients) {
        navigate("/clients");
        return;
      }
      await loadClientConfigurationPermissions();
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.error(
        error.response?.data?.message || "Failed to save permissions",
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle reset permissions
  const handleResetPermissions = () => {
    setClientConfigPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    toast("Permissions reset to original values");
  };

  // Group features by module
  const groupedByModule = {};
  allowedFeatures.forEach((feature) => {
    const moduleName = moduleMapping[feature] || "Other Module";
    if (!groupedByModule[moduleName]) {
      groupedByModule[moduleName] = [];
    }
    groupedByModule[moduleName].push(feature);
  });

  // Check if user is CHIEF_ADMIN (using normalized role check)
  const userRole = user?.role_name || user?.role;
  const normalizeRole = (role) =>
    role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
  const normalizedUserRole = normalizeRole(userRole);
  const isUserChiefAdmin = normalizedUserRole === "chief-admin";

  if (!isUserChiefAdmin) {
    return null;
  }

  // Check if clientId is missing
  if (!clientId) {
    return (
      <div className="min-h-screen bg-bg-secondary p-6">
        <div className="max-w-7xl mx-auto">
          <Card className="p-8 text-center">
            <p className="text-text-secondary mb-4">
              Client ID is required to view configuration.
            </p>
            <Button onClick={() => navigate("/clients")}>Go to Clients</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-secondary p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/clients")}
              className="hover:bg-bg-hover"
              aria-label="Back to All Clients"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-text-primary flex items-center space-x-2">
                <Shield className="w-6 h-6 text-primary-600" />
                <span>Client Configuration Permissions</span>
              </h1>
              <p className="text-sm text-text-secondary mt-1">
                Configure maximum allowed permissions for {clientName}
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 p-4 bg-primary-50 border-primary-200">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-primary-800">
              <p className="font-medium mb-1">Client Configuration Role</p>
              <p>
                This defines the maximum allowed permissions for all roles in
                this client. All other roles must be a subset of these
                permissions.
              </p>
            </div>
          </div>
        </Card>

        {/* Permissions Table */}
        {loading ? (
          <Card className="p-4">
            <TableSkeleton rows={10} columns={6} />
          </Card>
        ) : (
          <Card className="p-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Permission Name</TableHead>
                    <TableHead>View (default yes)</TableHead>
                    <TableHead>Add/Edit</TableHead>
                    <TableHead>Inactive</TableHead>
                    <TableHead>Permanently Delete</TableHead>
                    <TableHead>Close</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.keys(groupedByModule).map((moduleName) => (
                    <React.Fragment key={moduleName}>
                      <TableRow className="bg-bg-hover">
                        <TableCell
                          colSpan={6}
                          className="font-semibold text-text-primary"
                        >
                          {moduleName}
                        </TableCell>
                      </TableRow>
                      {groupedByModule[moduleName].map((feature) => {
                        const allowedActions = featureActionMap[feature] || [];
                        return (
                          <TableRow key={feature}>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-text-primary">
                                  {getFeatureDisplayName(feature)}
                                </span>
                                <Info
                                  className="w-4 h-4 text-text-tertiary"
                                  title={permissionDescriptions[feature]}
                                />
                              </div>
                            </TableCell>
                            {[
                              "view",
                              "add_edit",
                              "inactive",
                              "permanently_delete",
                              "close",
                            ].map((action) => {
                              const isAllowed = allowedActions.includes(action);
                              const isChecked =
                                isAllowed &&
                                getPermissionStatus(feature, action);

                              return (
                                <TableCell key={action}>
                                  {isAllowed ? (
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={() =>
                                        handlePermissionToggle(
                                          feature,
                                          action,
                                          isChecked,
                                        )
                                      }
                                    />
                                  ) : (
                                    <span className="text-text-tertiary">
                                      -
                                    </span>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end flex-wrap gap-3">
              {isFromAllClients && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/clients")}
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleResetPermissions}
                disabled={saving}
              >
                Reset
              </Button>
              <Button onClick={handleSavePermissions} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientConfigurationPage;
